/**
 * React Query hooks for the workspace agent registry, backed by the real Squad
 * agent API (`src/services/squadAgent.ts`) instead of the mock repository.
 *
 * Squad scopes by the per-org `saas-app-token`, resolved with the same
 * `useSquadAppToken` mechanism the home chat uses. The token is `undefined`
 * while `/applications` loads, so the queries stay disabled until it resolves.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult
} from '@tanstack/react-query'

import type { WorkspaceAgent } from 'src/api/facade/types'
import {
  addAgentAssignee,
  createAgent,
  deleteAgent,
  getAgent,
  listAgentAssignees,
  listAgents,
  removeAgentAssignee,
  revokeAutoApprovedTool,
  updateAgent,
  type SquadAgentDetail,
  type SquadAgentWriteInput
} from 'src/services/squadAgent'

import { useSquadAppToken } from '../../squad'

/** Maps a Squad agent record (snake_case) to the FE {@link WorkspaceAgent}. */
export const toWorkspaceAgent = (
  workspaceId: number,
  raw: SquadAgentDetail
): WorkspaceAgent => ({
  id: raw.id,
  workspaceId,
  name: raw.name,
  description: raw.description,
  allowedScopes: raw.allowed_scopes ?? '',
  systemPrompt: raw.system_prompt,
  provider: raw.provider,
  model: raw.model,
  temperature: raw.temperature,
  restricted: raw.restricted,
  tier: raw.tier,
  isPlatform: raw.is_platform,
  runAsExposure: raw.run_as_exposure,
  assignees: raw.assignees ?? [],
  toolExecutionMode: raw.tool_execution_mode ?? 'ask',
  autoApprovedTools: raw.auto_approved_tools ?? [],
  mcpServerCount: raw.mcp_server_count,
  canEdit: raw.can_edit,
  canRun: raw.can_run,
  isOwner: raw.is_owner,
  createdAt: raw.created_at,
  modifiedAt: raw.modified_at
})

export const agentQueryKeys = {
  list: (workspaceId: number) =>
    ['squad', 'agents', workspaceId, 'list'] as const,
  detail: (workspaceId: number, agentId: string) =>
    ['squad', 'agents', workspaceId, 'detail', agentId] as const
}

/** List the workspace's agents. Returns the resolved app-token alongside. */
export const useWorkspaceAgents = (
  workspaceId: number
): UseQueryResult<WorkspaceAgent[]> & { appToken: string | undefined } => {
  const appToken = useSquadAppToken(workspaceId)
  const result = useQuery({
    queryKey: agentQueryKeys.list(workspaceId),
    queryFn: async () => {
      const agents = await listAgents(appToken!)

      // The list serializer is lightweight; the detail fields default safely.
      return agents.map((a) =>
        toWorkspaceAgent(workspaceId, a as unknown as SquadAgentDetail)
      )
    },
    enabled: Boolean(appToken)
  })

  return { ...result, appToken }
}

/** Fetch one agent's full record (includes `allowed_scopes`). */
export const useWorkspaceAgent = (
  workspaceId: number,
  agentId: string | undefined
): UseQueryResult<WorkspaceAgent> & { appToken: string | undefined } => {
  const appToken = useSquadAppToken(workspaceId)
  const result = useQuery({
    queryKey: agentQueryKeys.detail(workspaceId, agentId ?? ''),
    queryFn: async () =>
      toWorkspaceAgent(workspaceId, await getAgent(appToken!, agentId!)),
    enabled: Boolean(appToken && agentId)
  })

  return { ...result, appToken }
}

const useInvalidate = (workspaceId: number) => {
  const queryClient = useQueryClient()

  return (agentId?: string) => {
    void queryClient.invalidateQueries({
      queryKey: agentQueryKeys.list(workspaceId)
    })
    if (agentId) {
      void queryClient.invalidateQueries({
        queryKey: agentQueryKeys.detail(workspaceId, agentId)
      })
    }
  }
}

export const useCreateAgent = (workspaceId: number) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidate(workspaceId)

  return useMutation({
    mutationFn: async (body: SquadAgentWriteInput) =>
      toWorkspaceAgent(workspaceId, await createAgent(appToken!, body)),
    onSuccess: (agent) => invalidate(agent.id)
  })
}

export const useUpdateAgent = (workspaceId: number, agentId: string) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidate(workspaceId)

  return useMutation({
    mutationFn: async (patch: SquadAgentWriteInput) =>
      toWorkspaceAgent(
        workspaceId,
        await updateAgent(appToken!, agentId, patch)
      ),
    onSuccess: () => invalidate(agentId)
  })
}

export const useDeleteAgent = (workspaceId: number) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidate(workspaceId)

  return useMutation({
    mutationFn: (agentId: string) => deleteAgent(appToken!, agentId),
    onSuccess: () => invalidate()
  })
}

/** Revoke ONE of the caller's own "Always approve" exceptions for `agentId`. */
export const useRevokeAutoApprovedTool = (
  workspaceId: number,
  agentId: string
) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidate(workspaceId)

  return useMutation({
    mutationFn: (toolName: string) =>
      revokeAutoApprovedTool(appToken!, agentId, toolName),
    onSuccess: () => invalidate(agentId)
  })
}

/* ------------------------------------------------------------------ *
 * Run-only assignees (viewer collaborators)
 * ------------------------------------------------------------------ */

export const useAgentAssignees = (workspaceId: number, agentId: string) => {
  const appToken = useSquadAppToken(workspaceId)

  return useQuery({
    queryKey: [...agentQueryKeys.detail(workspaceId, agentId), 'assignees'],
    queryFn: () => listAgentAssignees(appToken!, agentId),
    enabled: Boolean(appToken && agentId)
  })
}

export const useAddAgentAssignee = (workspaceId: number, agentId: string) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidate(workspaceId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userSsoId: string) =>
      addAgentAssignee(appToken!, agentId, userSsoId),
    onSuccess: () => {
      invalidate(agentId)
      void queryClient.invalidateQueries({
        queryKey: [...agentQueryKeys.detail(workspaceId, agentId), 'assignees']
      })
    }
  })
}

export const useRemoveAgentAssignee = (
  workspaceId: number,
  agentId: string
) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidate(workspaceId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (assigneeId: number) =>
      removeAgentAssignee(appToken!, agentId, assigneeId),
    onSuccess: () => {
      invalidate(agentId)
      void queryClient.invalidateQueries({
        queryKey: [...agentQueryKeys.detail(workspaceId, agentId), 'assignees']
      })
    }
  })
}
